import { ThresholdExpression } from '@agilelab/plugin-wb-governance-common';
import { WbSelect, WbTextField } from '@agilelab/plugin-wb-platform';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import { makeStyles, Theme, useTheme } from '@material-ui/core';
import { FC, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { MetricMetadataAddForm } from '../types';
import { MetricNumericFormatCustom } from './MetricNumericFormatCustom';

interface Line {
  width: number;
  color: string;
}

interface Threshold {
  value?: number;
  color: string;
  label: string;
}

const parseNumber = (value: string) => {
  return value === '' || value === undefined ? undefined : Number(value);
};

const useStyles = makeStyles((theme: Theme) => ({
  inputsRow: {
    display: 'flex',
    gap: 30,
    alignItems: 'baseline' as const,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column' as const,
      alignItems: 'stretch' as const,
    },
  },
  sliderContainer: {
    position: 'relative' as const,
    flex: 1,
  },
  boundInput: {
    width: 190,
    [theme.breakpoints.down('sm')]: {
      width: 120,
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  expressionRow: {
    display: 'flex',
    alignItems: 'baseline' as const,
    gap: 25,
    margin: '15px 0px',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column' as const,
      alignItems: 'stretch' as const,
    },
  },
  expressionSelect: {
    minWidth: 150,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
}));

export const MetricThresholdSlider: FC<
  UseFormReturn<MetricMetadataAddForm> & {
    colors: Array<{ label: string; value: string }>;
  }
> = props => {
  const theme = useTheme();
  const HEIGHT = 2;
  const BULLET = 16;
  const BULLET_OFFSET = BULLET / 2 - HEIGHT / 2;
  const DEFAULT_LINE_COLOR = theme.palette.grey[100];
  const classes = useStyles();

  const min = useMemo(() => parseNumber(props.watch('domain.min')), [props]);
  const max = useMemo(() => parseNumber(props.watch('domain.max')), [props]);
  const range = useMemo(() => props.watch('expression'), [props]);
  const thresholds: Threshold[] = useMemo(
    () =>
      (props.watch('thresholds') || []).map(t => ({
        ...t,
        value: parseNumber(t.value),
        color:
          props.colors.find(c => c.label === t.color)?.value ||
          DEFAULT_LINE_COLOR,
      })),
    [props, DEFAULT_LINE_COLOR],
  );

  const lines: Line[] = useMemo(() => {
    const isValid = min !== undefined && max !== undefined && range;

    if (isValid) {
      return (
        thresholds
          .filter(t => {
            if (t.value === undefined || !Boolean(t.color)) {
              return false;
            }

            switch (range) {
              case ThresholdExpression.Above:
                return t.value < max && t.value! >= min;
              case ThresholdExpression.AboveOrEqual:
                return t.value! <= max && t.value! >= min;
              case ThresholdExpression.Below:
                return t.value! <= max && t.value! > min;
              case ThresholdExpression.BelowOrEqual:
                return t.value! <= max && t.value! >= min;
              default:
                return t;
            }
          })
          .map(t => {
            const value = t.value || 0;

            const valueByRange: Record<ThresholdExpression, number> = {
              [ThresholdExpression.BelowOrEqual]: value - min,
              [ThresholdExpression.Below]: value - min,
              [ThresholdExpression.AboveOrEqual]: max - value,
              [ThresholdExpression.Above]: max - value,
            };

            const thresholdLine = max - min;
            const thresholdLineWidth = valueByRange[range];
            const width = (thresholdLineWidth / thresholdLine) * 100;

            return { width, color: t.color || DEFAULT_LINE_COLOR };
          })
          .sort((a, b) => a.width - b.width)
          // EACH LINE WIDTH = ITS WIDTH - PREV LINE WIDTH
          .map((l, index, array) => ({
            ...l,
            width: index > 0 ? l.width - array[index - 1].width : l.width,
          }))
      );
    }

    return [];
  }, [DEFAULT_LINE_COLOR, max, min, range, thresholds]);

  const isAbove = useMemo(
    () =>
      range &&
      (range === ThresholdExpression.Above ||
        range === ThresholdExpression.AboveOrEqual),
    [range],
  );

  return (
    <div>
      <div className={classes.inputsRow}>
        <Controller
          control={props.control}
          name="domain.min"
          render={({ field, fieldState: { error } }) => (
            <WbTextField
              label="Lower bound *"
              id="lowerBound"
              className={classes.boundInput}
              helperText={error?.message}
              error={Boolean(error)}
              {...field}
              InputProps={{
                inputComponent: MetricNumericFormatCustom as any,
              }}
            />
          )}
        />

        <div className={classes.sliderContainer}>
          {/* DEFAULT LINE */}
          <div
            style={{
              width: '100%',
              height: HEIGHT,
              background: DEFAULT_LINE_COLOR,
              position: 'absolute',
            }}
          />

          {/* RANGE LINES */}
          <div
            style={{
              display: 'flex',
              flexDirection: isAbove ? 'row-reverse' : 'row',
              marginLeft: 0,
            }}
          >
            {lines.map(line => (
              <div
                style={{
                  width: `${line.width}%`,
                  background: line.color,
                  height: HEIGHT,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    width: BULLET,
                    height: BULLET,
                    borderRadius: '100%',
                    background: line.color,
                    ...(isAbove
                      ? { left: -BULLET_OFFSET }
                      : { right: -BULLET_OFFSET }),
                    top: -BULLET_OFFSET,
                    zIndex: 2,
                    position: 'absolute',
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <Controller
          control={props.control}
          name="domain.max"
          render={({ field, fieldState: { error } }) => (
            <WbTextField
              label="Upper bound *"
              id="upperBound"
              className={classes.boundInput}
              helperText={error?.message}
              error={Boolean(error)}
              {...field}
              InputProps={{
                inputComponent: MetricNumericFormatCustom as any,
              }}
            />
          )}
        />
      </div>

      <div className={classes.expressionRow}>
        <Controller
          control={props.control}
          name="expression"
          render={({ field, fieldState: { error } }) => (
            <WbSelect
              id="expression"
              options={Object.values(ThresholdExpression)}
              getOptionLabel={v => `${snakeCaseToTitleCase(v)} the threshold`}
              className={classes.expressionSelect}
              label="Expression"
              helperText={error?.message}
              error={Boolean(error)}
              {...field}
            />
          )}
        />
      </div>
    </div>
  );
};
